import {DraggableAttributes, useDraggable, DraggableSyntheticListeners} from '@dnd-kit/core'
import {CSS} from '@dnd-kit/utilities'
import {DriveLayout} from '@domain/kiln-monitoring'
import {Box, Stack, Typography} from '@mui/material'
import {DrivePart} from '@widgets/kms'

import {getKilnPartDimensions, getKilnShellDimensions} from '../../helpers'
import {useKilnConfig} from '../../providers'

import {KilnPartDimensionsScale} from './common'
import {DragHandle} from './DragHandle'

interface DriveProps {
  positionYOffset: number
  isCurrentlySelected: boolean
  onClick: () => void
}

export const Drive: React.FC<DriveProps> = ({positionYOffset, isCurrentlySelected, onClick}) => {
  const {kilnConfiguration, kilnLayout} = useKilnConfig()

  const {metersInPixel} = getKilnShellDimensions(kilnConfiguration, kilnLayout)
  const {
    width: {widthInMeters}
  } = getKilnPartDimensions(kilnLayout.drive)

  const {attributes, listeners, setNodeRef, transform} = useDraggable({
    id: kilnLayout.drive.id
  })

  const style = {
    transform: CSS.Translate.toString(transform) // Apply transform for dragging
  }

  const xPosition =
    widthInMeters !== undefined
      ? kilnLayout.drive.x + (widthInMeters / 2) * metersInPixel
      : kilnLayout.drive.x

  return (
    <>
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          position: 'absolute',
          top: kilnLayout.drive.y + positionYOffset,
          left: xPosition,
          width: kilnLayout.drive.width,
          height: kilnLayout.drive.height
        }}
      >
        <DrivePart
          id={kilnLayout.drive.id}
          x={0}
          y={0}
          width={kilnLayout.drive.width}
          height={kilnLayout.drive.height}
          position={kilnLayout.drive.position}
          // default "side" is left
          flip={
            kilnLayout.drive.side === 'right' ||
            (!kilnLayout.drive.side && kilnLayout.materialFlow !== 'rightToLeft') // this is for backwards compatibility
          }
        />
      </Box>
      <DriveOverlay
        driveLayout={kilnLayout.drive}
        style={style}
        attributes={attributes}
        listeners={listeners}
        isCurrentlySelected={isCurrentlySelected}
        onClick={onClick}
      />
    </>
  )
}

type DriveOverlayProps = {
  driveLayout: DriveLayout
  style: {
    transform: string | undefined
  }
  isCurrentlySelected: boolean
  attributes: DraggableAttributes
  listeners: DraggableSyntheticListeners
  onClick: () => void
}

const DriveOverlay = ({
  driveLayout,
  style,
  isCurrentlySelected,
  attributes,
  listeners,
  onClick
}: DriveOverlayProps) => {
  const {kilnLayout} = useKilnConfig()

  const {
    width: {isWidthEmpty}
  } = getKilnPartDimensions(kilnLayout.drive)

  return (
    <Box
      style={style}
      sx={{
        position: 'absolute',
        border: '2px solid rgba(41, 51, 61, 0.2)',
        borderRadius: '8px',
        height: kilnLayout.interactiveOverlayHeight,
        width: 192,
        top: 0,
        left: kilnLayout.drive.x - 25,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: 'pointer',
        p: '8px',
        ...(isCurrentlySelected
          ? {border: '2px solid #016AD4', background: 'rgba(230, 240, 251, 0.4)'}
          : {})
      }}
      data-test-id="drive-overlay"
      onClick={onClick}
    >
      <Stack direction="row" alignItems="center" spacing={3}>
        <DragHandle isDisabled={isWidthEmpty} attributes={attributes} listeners={listeners} />
        <Typography noWrap>{driveLayout.name ?? ''}</Typography>
      </Stack>
      <KilnPartDimensionsScale selectedKilnPart={kilnLayout.drive} />
    </Box>
  )
}
